import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { loginWithToken, hydrateSession, selectIsAuthenticated, selectIsPlatform, logout, setCredentials, clearAuth } from './features/auth/authSlice';
import { RequirePermission } from './components/RequirePermission';

// Layouts
import AdminLayout from './layouts/AdminLayout';
import AuthLayout from './layouts/AuthLayout';

// Pages
import LoginPage from './pages/auth/LoginPage';
import SSOCallbackPage from './pages/auth/SSOCallbackPage';
import AcceptInvitePage from './pages/auth/AcceptInvitePage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import OperatorsPage from './pages/operators/OperatorsPage';
import OperatorTokensPage from './pages/operators/OperatorTokensPage';
import DashboardPage from './pages/DashboardPage';
import { TenantsPage } from './pages/admin/TenantsPage';
import TenantDetailsPage from './pages/tenants/TenantDetailsPage';
import TenantOnboardingPage from './pages/tenants/TenantOnboardingPage';
import TenantApplicationsPage from './pages/tenants/TenantApplicationsPage';
import { UsersManagementPage } from './pages/admin/UsersManagementPage';
import SettingsPage from './pages/settings/SettingsPage';
import { MonitoringPage } from './pages/admin/MonitoringPage';
import { EventBusPage } from './pages/admin/EventBusPage';
import { FeatureFlagsPage } from './pages/admin/FeatureFlagsPage';
import { AiPlatformKeysPage } from './pages/admin/AiPlatformKeysPage';
import { GrowthAnalyticsPage } from './pages/admin/GrowthAnalyticsPage';
import { DanaAiOpsPage } from './pages/admin/DanaAiOpsPage';
import { SecurityPage } from './pages/admin/SecurityPage';
import { AuditLogPage } from './pages/admin/AuditLogPage';
import { AccessChangesPage } from './pages/admin/AccessChangesPage';
import { SessionsPage } from './pages/admin/SessionsPage';
import { SystemPage } from './pages/admin/SystemPage';
import ScheduledJobsPage from './pages/admin/ScheduledJobsPage';
import ReportsPage from './pages/reports/ReportsPage';
import SupportCasesPage from './pages/support/SupportCasesPage';
import IncidentsPage from './pages/support/IncidentsPage';
import AnnouncementsPage from './pages/support/AnnouncementsPage';
import BulkImportPage from './pages/employees/BulkImportPage';
import EmployeeManagementPage from './pages/employees/EmployeeManagementPage';
import AiAssistantPage from './pages/settings/AiAssistantPage';
import TicketingIntegrationPage from './pages/settings/TicketingIntegrationPage';
import ComplianceCenterPage from './pages/compliance/ComplianceCenterPage';
import DataGovernanceDashboardPage from './pages/compliance/DataGovernanceDashboardPage';
import RetentionPoliciesPage from './pages/compliance/RetentionPoliciesPage';
import LegalHoldsPage from './pages/compliance/LegalHoldsPage';
import ErasureRequestsPage from './pages/compliance/ErasureRequestsPage';
import RetentionDryRunPage from './pages/compliance/RetentionDryRunPage';
import AuditEvidencePage from './pages/compliance/AuditEvidencePage';
import TenantLifecyclePage from './pages/compliance/TenantLifecyclePage';
import EmployeeLifecyclePage from './pages/compliance/EmployeeLifecyclePage';
import { DeliverabilityPage } from './pages/admin/DeliverabilityPage';
import { PrivacyOpsPage } from './pages/admin/PrivacyOpsPage';
import { BillingPage } from './pages/billing/BillingPage';
import { PaymentsPage } from './pages/payments/PaymentsPage';
import { PaymentCollectionPage } from './pages/payments/PaymentCollectionPage';
import { PaymentRoutingPage } from './pages/payments/PaymentRoutingPage';
import { ConnectionProfilesPage } from './pages/payments/ConnectionProfilesPage';
import { EverydayServicesPage } from './pages/commerce/EverydayServicesPage';
import { PaymentAccountingPage } from './pages/payments/PaymentAccountingPage';
import { PaymentOpsDashboardPage } from './pages/payments/PaymentOpsDashboardPage';
import { MarketplaceLaunchPage } from './pages/admin/MarketplaceLaunchPage';
import { PaymentTaxRulesPage } from './pages/tax/PaymentTaxRulesPage';
import { CountryPacksPage } from './pages/tax/CountryPacksPage';
import { HolidayPacksPage } from './pages/tax/HolidayPacksPage';
import ExportTemplatesPage from './pages/tax/ExportTemplatesPage';
import { CountriesPage } from './pages/countries/CountriesPage';
import { DeveloperPlatformPage } from './pages/developer/DeveloperPlatformPage';
import { ConnectorCatalogPage } from './pages/developer/ConnectorCatalogPage';
import { SandboxTenantsPage } from './pages/developer/SandboxTenantsPage';
import { CertificationsPage } from './pages/developer/CertificationsPage';
import { AiClientsPage } from './pages/developer/AiClientsPage';
import { ConnectorIncidentsPage } from './pages/developer/ConnectorIncidentsPage';
import MarketplaceReviewPage from './pages/marketplace/MarketplaceReviewPage';
import PartnerProductReviewPage from './pages/credit/PartnerProductReviewPage';
import PartnerApplicationsPage from './pages/credit/PartnerApplicationsPage';
import PartnerNetworkPage from './pages/credit/PartnerNetworkPage';
import PartnerCapabilitiesPage from './pages/credit/PartnerCapabilitiesPage';
import RolesPermissionsPage from './features/rbac/RolesPermissionsPage';

// Shown when an authenticated but non-platform user (e.g. a company admin / HR)
// reaches the operator console. Under Model A they manage their company in the
// KaziPeople app, not here.
const NotPlatformAccess: React.FC = () => {
  const dispatch = useAppDispatch();
  const mainAppUrl = (import.meta as any).env?.VITE_MAIN_APP_URL || 'http://localhost:3000';
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Result
        status="403"
        title="This console is for KaziPeople platform staff"
        subTitle="Manage your company — people, leave and settings — in the KaziPeople app."
        extra={[
          <Button type="primary" key="app" href={mainAppUrl}>
            Open KaziPeople
          </Button>,
          <Button key="logout" onClick={() => dispatch(logout())}>
            Sign out
          </Button>,
        ]}
      />
    </div>
  );
};

// Protected Route Component — admin-portal is the platform operator console, so
// only platform-scoped users may enter.
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isPlatform = useAppSelector(selectIsPlatform);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but tenant-scoped (company admin / HR / employee): they belong
  // in the KaziPeople app, not the operator console.
  if (!isPlatform) {
    return <NotPlatformAccess />;
  }

  return <>{children}</>;
};

function App() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const handleDemoLogin = () => {
    // Set demo credentials for development
    const demoUser = {
      id: '1',
      email: 'admin@amashr.com',
      name: 'Admin User',
      role: 'system-admin' as const,
      permissions: ['all'],
    };
    const demoToken = 'demo-token-' + Date.now();
    
    dispatch(setCredentials({ user: demoUser, token: demoToken }));
    localStorage.setItem('admin_token', demoToken);
    localStorage.setItem('admin_user', JSON.stringify(demoUser));
  };

  useEffect(() => {
    // 1) SSO entry from main-app: ?token=<real auth-service JWT>.
    //    Skip the invite-accept page — its ?token is an invitation token, not a JWT,
    //    and must not be consumed/stripped here.
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token && location.pathname !== '/invite/accept') {
      dispatch(loginWithToken(token)).finally(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      });
      return;
    }

    // 2) Offline/UI dev escape hatch only.
    if (import.meta.env.VITE_USE_DEMO === 'true' && !localStorage.getItem('admin_user')) {
      handleDemoLogin();
      return;
    }

    // 3) P2-8: restore a REAL session from the httpOnly cookie via /session.
    //    On failure the thunk clears auth → the user lands on the login page.
    dispatch(hydrateSession());
  }, [dispatch, location]);

  return (
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sso-callback" element={<SSOCallbackPage />} />
        {/* Public: an invited operator sets their password here. */}
        <Route path="/invite/accept" element={<AcceptInvitePage />} />
        {/* Public: self-service password recovery. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Protected Admin Routes */}
      <Route
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        {/* Any signed-in operator can change their own password. */}
        <Route path="/change-password" element={<ChangePasswordPage />} />
        
        {/* Tenant Management */}
        <Route path="/tenants" element={<RequirePermission perm="tenant:read:all"><TenantsPage /></RequirePermission>} />
        <Route path="/tenants/onboarding" element={<RequirePermission perm="tenant:create"><TenantOnboardingPage /></RequirePermission>} />
        {/* static path before :tenantId so it isn't captured as an id */}
        <Route path="/tenants/applications" element={<RequirePermission perm="tenant:create"><TenantApplicationsPage /></RequirePermission>} />
        <Route path="/tenants/:tenantId" element={<RequirePermission perm="tenant:read:all"><TenantDetailsPage /></RequirePermission>} />

        {/* User Management */}
        <Route path="/users" element={<RequirePermission perm="user:read:all"><UsersManagementPage /></RequirePermission>} />

        {/* Roles & Permissions — dynamic-RBAC platform role builder + catalog viewer */}
        <Route path="/roles" element={<RequirePermission perm="feature-flag:manage"><RolesPermissionsPage /></RequirePermission>} />

        {/* Platform operators (owner-only) */}
        <Route path="/operators" element={<RequirePermission perm="platform-staff:manage"><OperatorsPage /></RequirePermission>} />
        <Route path="/operators/tokens" element={<OperatorTokensPage />} />

        {/* Template marketplace verification (platform operators) */}
        <Route path="/marketplace" element={<RequirePermission perm="tenant:read:all"><MarketplaceReviewPage /></RequirePermission>} />

        {/* Partner loan-product review queue (M1) */}
        <Route path="/credit/products-review" element={<RequirePermission perm="tenant:read:all"><PartnerProductReviewPage /></RequirePermission>} />
        <Route path="/credit/partner-network" element={<RequirePermission perm="tenant:read:all"><PartnerNetworkPage /></RequirePermission>} />
        <Route path="/credit/partner-applications" element={<RequirePermission perm="tenant:read:all"><PartnerApplicationsPage /></RequirePermission>} />
        <Route path="/credit/partner-capabilities" element={<RequirePermission perm="tenant:read:all"><PartnerCapabilitiesPage /></RequirePermission>} />

        {/* Employee Management */}
        <Route path="/employees" element={<RequirePermission perm="employee:read"><EmployeeManagementPage /></RequirePermission>} />
        <Route path="/employees/import" element={<RequirePermission perm="employee:import"><BulkImportPage /></RequirePermission>} />

        {/* Billing & subscriptions */}
        <Route path="/billing" element={<RequirePermission perm="billing:read:all"><BillingPage /></RequirePermission>} />
        {/* KaziPeople's own M-Pesa collection credentials (writes are owner/admin-only server-side) */}
        <Route path="/billing/collection" element={<RequirePermission perm="billing:read:all"><PaymentCollectionPage /></RequirePermission>} />

        {/* Payment providers — collections + disbursements status */}
        <Route path="/payments" element={<RequirePermission perm="billing:read:all"><PaymentsPage /></RequirePermission>} />
        <Route path="/payments/routing" element={<RequirePermission perm="tenant:read:all"><PaymentRoutingPage /></RequirePermission>} />
        <Route path="/payments/connection-profiles" element={<RequirePermission perm="tenant:read:all"><ConnectionProfilesPage /></RequirePermission>} />
        <Route path="/payments/accounting" element={<RequirePermission perm="tenant:read:all"><PaymentAccountingPage /></RequirePermission>} />
        <Route path="/payments/ops" element={<RequirePermission perm="tenant:read:all"><PaymentOpsDashboardPage /></RequirePermission>} />
        {/* Everyday Services (commerce) — platform kill switches + reconciliation queue */}
        <Route path="/everyday-services" element={<RequirePermission perm="commerce:ops"><EverydayServicesPage /></RequirePermission>} />
        <Route path="/marketplace-launch" element={<RequirePermission perm="tenant:read:all"><MarketplaceLaunchPage /></RequirePermission>} />
        {/* Country-pack tax-rule administration */}
        <Route path="/countries" element={<RequirePermission perm="country_pack:read"><CountriesPage /></RequirePermission>} />
        <Route path="/country-packs" element={<RequirePermission perm="tenant:read:all"><CountryPacksPage /></RequirePermission>} />
        <Route path="/holiday-packs" element={<RequirePermission perm="tenant:read:all"><HolidayPacksPage /></RequirePermission>} />
        <Route path="/export-templates" element={<RequirePermission perm="tenant:read:all"><ExportTemplatesPage /></RequirePermission>} />
        <Route path="/tax-rules" element={<RequirePermission perm="tenant:read:all"><PaymentTaxRulesPage /></RequirePermission>} />

        {/* Developer Platform — tenant API clients across the platform */}
        <Route path="/developer" element={<RequirePermission perm="tenant:read:all"><DeveloperPlatformPage /></RequirePermission>} />
        <Route path="/developer/connectors" element={<RequirePermission perm="tenant:read:all"><ConnectorCatalogPage /></RequirePermission>} />
        <Route path="/developer/sandboxes" element={<RequirePermission perm="tenant:create"><SandboxTenantsPage /></RequirePermission>} />
        <Route path="/developer/certifications" element={<RequirePermission perm="tenant:update:all"><CertificationsPage /></RequirePermission>} />
        <Route path="/developer/ai-clients" element={<RequirePermission perm="tenant:update:all"><AiClientsPage /></RequirePermission>} />
        <Route path="/developer/connector-incidents" element={<RequirePermission perm="tenant:read:all"><ConnectorIncidentsPage /></RequirePermission>} />

        {/* Compliance Center — platform governance of legal documents */}
        <Route path="/compliance" element={<RequirePermission perm="auditlog:read:all"><ComplianceCenterPage /></RequirePermission>} />
        <Route path="/compliance/privacy" element={<RequirePermission perm="auditlog:read:all"><PrivacyOpsPage /></RequirePermission>} />
        <Route path="/compliance/governance" element={<RequirePermission perm="compliance:dashboard:view"><DataGovernanceDashboardPage /></RequirePermission>} />
        <Route path="/compliance/retention-policies" element={<RequirePermission perm="compliance:retention_policy:view"><RetentionPoliciesPage /></RequirePermission>} />
        <Route path="/compliance/legal-holds" element={<RequirePermission perm="compliance:legal_hold:view"><LegalHoldsPage /></RequirePermission>} />
        <Route path="/compliance/erasure-requests" element={<RequirePermission perm="compliance:erasure_request:view"><ErasureRequestsPage /></RequirePermission>} />
        <Route path="/compliance/dry-run" element={<RequirePermission perm="compliance:dry_run:view"><RetentionDryRunPage /></RequirePermission>} />
        <Route path="/compliance/audit-evidence" element={<RequirePermission perm="compliance:audit_evidence:view"><AuditEvidencePage /></RequirePermission>} />
        <Route path="/compliance/tenant-lifecycle" element={<RequirePermission perm="tenant_lifecycle:view"><TenantLifecyclePage /></RequirePermission>} />
        <Route path="/compliance/employee-lifecycle" element={<RequirePermission perm="employee_lifecycle:view"><EmployeeLifecyclePage /></RequirePermission>} />

        {/* System Administration */}
        <Route path="/system/monitoring" element={<RequirePermission perm="monitoring:read"><MonitoringPage /></RequirePermission>} />
        <Route path="/system/eventbus" element={<RequirePermission perm="monitoring:read"><EventBusPage /></RequirePermission>} />
        <Route path="/system/feature-flags" element={<RequirePermission perm="feature-flag:manage"><FeatureFlagsPage /></RequirePermission>} />
        <Route path="/system/ai-keys" element={<RequirePermission perm="feature-flag:manage"><AiPlatformKeysPage /></RequirePermission>} />
        <Route path="/analytics/growth" element={<RequirePermission perm="analytics:read"><GrowthAnalyticsPage /></RequirePermission>} />
        <Route path="/ai-ops" element={<RequirePermission perm="analytics:read"><DanaAiOpsPage /></RequirePermission>} />
        <Route path="/system" element={<RequirePermission perm="monitoring:read"><SystemPage /></RequirePermission>} />
        <Route path="/system/security" element={<RequirePermission perm="monitoring:read"><SecurityPage /></RequirePermission>} />
        <Route path="/security/sessions" element={<RequirePermission perm="monitoring:read"><SessionsPage /></RequirePermission>} />
        <Route path="/security/access-changes" element={<RequirePermission perm="auditlog:read:all"><AccessChangesPage /></RequirePermission>} />
        <Route path="/system/audit" element={<RequirePermission perm="auditlog:read:all"><AuditLogPage /></RequirePermission>} />
        <Route path="/system/deliverability" element={<RequirePermission perm="monitoring:read"><DeliverabilityPage /></RequirePermission>} />
        <Route path="/system/scheduler" element={<RequirePermission perm="integration:manage"><ScheduledJobsPage /></RequirePermission>} />

        {/* Reports & Analytics */}
        <Route path="/reports" element={<RequirePermission perm="analytics:read"><ReportsPage /></RequirePermission>} />
        <Route path="/support/cases" element={<RequirePermission perm="support:read"><SupportCasesPage /></RequirePermission>} />
        <Route path="/support/incidents" element={<RequirePermission perm="incident:read"><IncidentsPage /></RequirePermission>} />
        <Route path="/support/announcements" element={<RequirePermission perm="announcement:manage"><AnnouncementsPage /></RequirePermission>} />
        
        {/* Settings */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/ai-assistant" element={<AiAssistantPage />} />
        <Route path="/settings/ticketing" element={<RequirePermission perm="support_integration:manage"><TicketingIntegrationPage /></RequirePermission>} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;